<script setup lang="ts">

import { ref } from 'vue'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import { handleNext } from '../tutorialSteps'
import { logger } from '@/models/logger';
const videoRef = ref<HTMLVideoElement>()
const isPlaying = ref(false)
const hasEnded = ref(false)
const hasError = ref(false)
const isLoading = ref(true)

const replayVideo = () => {
  if (videoRef.value) {
    videoRef.value.currentTime = 0
    videoRef.value.play().catch(err => {
      logger.error('Video playback failed:', err)
      hasError.value = true
    })
    isPlaying.value = true
    hasEnded.value = false
  }
}

const handleVideoEnd = () => {
  isPlaying.value = false
  hasEnded.value = true
}

const handleVideoError = (e: Event) => {
  logger.error('Video failed to load:', e)
  hasError.value = true
  isLoading.value = false
}

const handleVideoLoaded = () => {
  isLoading.value = false
}

const handleNextStep = async () => {
  await handleNext('tutorial_intro')
}
</script>

<template>
  <div class="">
    <div class="relative bg-slate-900 min-h-[300px] flex items-center justify-center">
      <!-- Video element -->
      <video
        v-if="!hasError"
        ref="videoRef"
        playsinline
        preload="auto"
        class="w-full h-auto object-top"
        :class="{ 'brightness-50': !isPlaying }"
        @ended="handleVideoEnd"
        @error="handleVideoError"
        @loadeddata="handleVideoLoaded"
      >
        <source src="https://cdn.ferion.com/videos/misc/intro.mp4" type="video/mp4">
        <source src="https://cdn.ferion.com/videos/misc/intro.webm" type="video/webm">
        Your browser does not support the video tag.
      </video>

      <!-- Loading state -->
      <div
        v-if="isLoading && !hasError"
        class="absolute inset-0 flex items-center justify-center bg-slate-900"
      >
        <div class="text-white text-center">
          <div class="animate-spin rounded-full h-16 w-16 border-b-2 border-white mx-auto mb-4"></div>
          <p>Loading video...</p>
        </div>
      </div>

      <!-- Error state -->
      <div
        v-if="hasError"
        class="absolute inset-0 flex items-center justify-center bg-slate-900 text-white text-center p-4"
      >
        <div>
          <svg
            class="w-16 h-16 text-red-500 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p class="text-lg mb-2">Video failed to load</p>
          <p class="text-sm text-slate-400">Please check your internet connection or try again later</p>
        </div>
      </div>

      <!-- Play button overlay -->
      <div
        v-if="!isPlaying && !hasError && !isLoading"
        @click="replayVideo"
        class="absolute inset-0 flex items-center justify-center cursor-pointer group"
      >
        <div class="bg-black/50 rounded-full p-4 group-hover:bg-black/70 transition-colors">
          <svg
            class="w-16 h-16 text-white"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      </div>
    </div>

    <div class="bg-slate-800 flex justify-end p-2 rounded-b">
      <ActionButton @click="handleNextStep">Let's Goooo!</ActionButton>
    </div>
  </div>
</template>

<style scoped lang="scss">

</style>